import axios from "axios";
import Swal from "sweetalert2";
import {
  ADMIN_LOGIN_REQUEST,
  ADMIN_LOGIN_FAIL,
  ADMIN_LOGIN_SUCCESS,
  ADMIN_LOGOUT,
  FORGOT_PASSWORD_REQUEST,
  FORGOT_PASSWORD_FAIL,
  FORGOT_PASSWORD_SUCCESS,
  RESET_PASSWORD_REQUEST,
  RESET_PASSWORD_SUCCESS,
  RESET_PASSWORD_FAIL,
  USER_VIEW_SUCCESS,
  USER_VIEW_REQUEST,
  USER_VIEW_FAIL,
  VIEW_ACCREDITED_INVESTORS,
  VIEW_ACCREDITED_INVESTORS_FAIL,
  VIEW_ACCREDITED_INVESTORS_SUCCESS,
  VIEW_COUNTRY_DETAILS,
  VIEW_COUNTRY_DETAILS_FAIL,
  VIEW_COUNTRY_DETAILS_SUCCESS,

  USER_VIEW_INVESTMENT_SUCCESS,
  USER_VIEW_INVESTMENT_REQUEST,
  USER_VIEW_INVESTMENT_FAIL,
  VIEW_FOLLOWER_REPORT_SUCCESS,
  VIEW_FOLLOWER_REPORT_REQUEST,
  VIEW_FOLLOWER_REPORT_FAIL,
} from "../constants/adminConstants";

export const login = (email, password, navigate) => async (dispatch) => {
  try {
    dispatch({ type: ADMIN_LOGIN_REQUEST });
    const config = {
      headers: {
        "Content-type": "application/json",
      },
    };
    const { data } = await axios.post(
      process.env.REACT_APP_API + "login-admin",
      { email, password },
      config
    );
    dispatch({ type: ADMIN_LOGIN_SUCCESS, payload: data });
    if (data.status == 200) {
      localStorage.setItem("admin", JSON.stringify(data));
      localStorage.setItem("accessToken", data.data.accessToken);
      Swal.fire({
        title: data.message,
        timer: 2500,
        timerProgressBar: true,
        icon: "success",
        confirmButtonText: "OK",
        showCloseButton: true,
      });
      // Bug #17 fix — force a full page reload to wipe any in-memory Redux
      // slices from a prior user's session (e.g. /dr/credentials data from
      // an expired DR persisting after logging in as a different DR). The
      // SweetAlert success modal dismisses after 2.5s; reloading after the
      // dispatch sends the user to a fresh React tree on /. Plain
      // navigate("/") leaves Redux state intact and lets the prior user's
      // data leak through useRole.js:17-29 which reads Redux first.
      window.location.assign(window.location.origin + window.location.pathname + '#/');
      window.location.reload();
    }
  } catch (error) {
    if (error?.response?.data?.message) {
      Swal.fire({
        title: "Wrong email and password",
        timer: 2500,
        timerProgressBar: true,
        icon: "error",
        confirmButtonText: "OK",
        showCloseButton: true,
      });
    }

    dispatch({
      type: ADMIN_LOGIN_FAIL,
      payload:
        error?.response?.data?.message
          ? error.response.data.message
          : error.message,
    });
  }
};

export const logout = (navigate) => async (dispatch) => {
  localStorage.removeItem("accessToken");
  dispatch({ type: ADMIN_LOGOUT });
  // Bug #17 fix — match the login-side reload so the next user's session
  // starts on a clean Redux store. Plain navigate("/login") would let the
  // previous user's slice data linger in memory until manually refreshed.
  window.location.assign(window.location.origin + window.location.pathname + '#/login');
  window.location.reload();
};

export const forgot_password = (email, clear) => async (dispatch) => {
  try {
    dispatch({ type: FORGOT_PASSWORD_REQUEST });
    const config = {
      headers: {
        "Content-type": "application/json",
      },
    };
    const { data } = await axios.post(
      process.env.REACT_APP_API + "forgot-password",
      { email },
      config
    );
    dispatch({ type: FORGOT_PASSWORD_SUCCESS, payload: data });

    if (data.status == 200) {
      Swal.fire({
        title: data.message,
        timer: 6500,
        timerProgressBar: true,
        icon: "success",
        confirmButtonText: "OK",
        showCloseButton: true,
      });
      clear();
    }
  } catch (error) {
    dispatch({
      type: FORGOT_PASSWORD_FAIL,
      payload:
        error?.response?.data?.message
          ? error.response.data.message
          : error.message,
    });
  }
};

export const reset_password =
  (token, newPassword, clear, redirect) => async (dispatch) => {
    try {
      dispatch({ type: RESET_PASSWORD_REQUEST });
      const config = {
        headers: {
          "Content-type": "application/json",
        },
      };
      const { data } = await axios.post(
        process.env.REACT_APP_API + "set-password",
        { token, newPassword },
        config
      );
      dispatch({ type: RESET_PASSWORD_SUCCESS, payload: data });
      if (data.status === 200) {
        Swal.fire({
          title: data.message,
          timer: 6500,
          timerProgressBar: true,
          icon: "success",
          confirmButtonText: "OK",
          showCloseButton: true,
        });
        redirect();
        clear();
      }
    } catch (error) {
      if (error?.response?.data?.message) {
        Swal.fire({
          title: error?.response?.data?.message,
          timer: 2500,
          timerProgressBar: true,
          icon: "error",
          confirmButtonColor: "#f27474",
          confirmButtonText: "OK",
          dangerMode: true,
          showCloseButton: true,
        });
        redirect();
      }
      dispatch({
        type: RESET_PASSWORD_FAIL,
        payload:
          error?.response?.data?.message
            ? error.response.data.message
            : error.message,
      });
    }
  };

export const getUserDetails = (token, id) => async (dispatch) => {
  try {
    dispatch({ type: USER_VIEW_REQUEST });
    const config = {
      headers: {
        "x-access-token": token,
      },
    };
    const { data } = await axios.post(
      process.env.REACT_APP_API + "get-user-details-byid",
      { user_id: id.userId },
      config
    );
    dispatch({ type: USER_VIEW_SUCCESS, payload: data });
  } catch (error) {
    // A 403 here is a role telling the truth, not a fault: a compliance
    // session that may decide on a user cannot necessarily read the whole
    // profile. A modal demanding acknowledgement of an expected refusal
    // trains admins to dismiss dialogs, so the page renders it as a line
    // instead (Viewuser reads USER_VIEW_FAIL).
    if (error?.response?.status !== 403) {
      Swal.fire({
        title: error?.response?.data?.message || "An error occurred",
        timerProgressBar: true,
        icon: "error",
        confirmButtonText: "OK",
        showCloseButton: true,
      });
    }
    dispatch({
      type: USER_VIEW_FAIL,
      payload:
        error?.response?.data?.message
          ? error.response.data.message
          : error.message,
    });
  }
};

export const getAccreditedInvestorsDetails =
  (token, id) => async (dispatch) => {
    try {
      dispatch({ type: VIEW_ACCREDITED_INVESTORS });
      const config = {
        headers: {
          "x-access-token": token,
        },
      };
      const { data } = await axios.post(
        process.env.REACT_APP_API + "admin/get-accrediteduserdetail-byid",
        { id: id.userId },
        config
      );
      dispatch({ type: VIEW_ACCREDITED_INVESTORS_SUCCESS, payload: data });
    } catch (error) {
      Swal.fire({
        title: error?.response?.data?.message || "An error occurred",
        timerProgressBar: true,
        icon: "error",
        confirmButtonText: "OK",
        showCloseButton: true,
      });
      dispatch({
        type: VIEW_ACCREDITED_INVESTORS_FAIL,
        payload:
          error?.response?.data?.message
            ? error.response.data.message
            : error.message,
      });
    }
  };

//get-country-list
export const getCountryList = (token) => async (dispatch) => {
  try {
    dispatch({ type: VIEW_COUNTRY_DETAILS });
    const config = {
      headers: {
        "x-access-token": token,
      },
    };
    const { data } = await axios.get(
      process.env.REACT_APP_API + "get-country",
      config
    );
    dispatch({ type: VIEW_COUNTRY_DETAILS_SUCCESS, payload: data });
  } catch (error) {
    Swal.fire({
      title: error?.response?.data?.message || "An error occurred",
      timerProgressBar: true,
      icon: "error",
      confirmButtonText: "OK",
      showCloseButton: true,
    });
    dispatch({
      type: VIEW_COUNTRY_DETAILS_FAIL,
      payload:
        error?.response?.data?.message
          ? error.response.data.message
          : error.message,
    });
  }
};


//get-investment-details
export const getUserInvestmentDetails = (token, id) => async (dispatch) => {
  try {
    dispatch({ type: USER_VIEW_INVESTMENT_REQUEST });
    const config = {
      headers: {
        "x-access-token": token,
      },
    };
    const { data } = await axios.post(
      process.env.REACT_APP_API + "admin/get-user-campaign-investment",
      { user_id: id.reportId },
      config
    );
    dispatch({ type: USER_VIEW_INVESTMENT_SUCCESS, payload: data });
  } catch (error) {
    Swal.fire({
      title: error?.response?.data?.message || "An error occurred",
      timerProgressBar: true,
      icon: "error",
      confirmButtonText: "OK",
      showCloseButton: true,
    });
    dispatch({
      type: USER_VIEW_INVESTMENT_FAIL,
      payload:
        error?.response?.data?.message
          ? error.response.data.message
          : error.message,
    });
  }
};

//get-designation-report-details
export const getFollowersDetails = (token, id) => async (dispatch) => {
  try {
    dispatch({ type: VIEW_FOLLOWER_REPORT_REQUEST });
    const config = {
      headers: {
        "x-access-token": token,
      },
    };
    const { data } = await axios.post(
      process.env.REACT_APP_API + "getFollowersByManagerId",
      { user_id: id.reportId },
      config
    );
    dispatch({ type: VIEW_FOLLOWER_REPORT_SUCCESS, payload: data });
  } catch (error) {
    Swal.fire({
      title: error?.response?.data?.message || "An error occurred",
      timerProgressBar: true,
      icon: "error",
      confirmButtonText: "OK",
      showCloseButton: true,
    });
    dispatch({
      type: VIEW_FOLLOWER_REPORT_FAIL,
      payload:
        error?.response?.data?.message
          ? error.response.data.message
          : error.message,
    });
  }
};
